
import Vue, { PropType } from 'vue';
import BetaPluginDialog from './BetaPluginDialog.vue';

export default Vue.extend({
	name: 'PluginCard',
	props: {
		pluginName: {
			type: String,
			required: true,
		},
		pluginIcon: {
			type: String,
			required: true,
		},
		description: {
			type: String,
			required: true,
		},
		// Plugin lifecycle status: 'coming-soon' (warning dot, disabled CTA), 'beta' (lead dot, opens the beta acknowledgement dialog), or 'launched' (success dot, opens the marketplace listing directly).
		status: {
			type: String,
			default: 'coming-soon',
			validator: (value: string) => ['coming-soon', 'beta', 'launched'].includes(value),
		},
		// When true, the CTA is disabled with the `disabledTooltip` explanation instead of
		// opening the beta dialog / the marketplace listing. Ignored for 'coming-soon' (already
		// disabled by status). The tutorial link stays active.
		//
		// NOT currently set by anything: the Plugins page hides a plugin the role isn't
		// granted rather than showing it disabled, so nothing renders this path today.
		// Kept as component API for a surface that wants show-but-disable.
		disabled: {
			type: Boolean,
			default: false,
		},
		// Tooltip copy shown on the disabled CTA. Defaults to the standard enterprise-denied
		// message (aligned with the server PLUGIN_NOT_ALLOWED 403); override per surface if needed.
		disabledTooltip: {
			type: String,
			default: 'This plugin isn\'t enabled for your team. Contact your administrator to request access.',
		},
		// Bug / feedback report form opened from inside the beta acknowledgement dialog.
		feedbackUrl: {
			type: String,
			default: '',
		},
		// Marketplace listing opened in a new tab as the primary call-to-action in the beta acknowledgement dialog.
		marketplaceUrl: {
			type: String,
			default: '',
		},
		// Label for the marketplace call-to-action button — varies by marketplace (e.g. 'Get it on Adobe Exchange', 'Get it on the Autodesk App Store').
		marketplaceCtaText: {
			type: String,
			default: 'Get the Plugin',
		},
		// Optional external tutorial / walkthrough link shown next to "Get Started". Only rendered when present.
		tutorialUrl: {
			type: String,
			default: '',
		},
		// Numbered install steps shown in the beta acknowledgement dialog. Not rendered when empty.
		installSteps: {
			type: Array as PropType<string[]>,
			default: () => [],
		},
		// Optional public web page for the plugin, linked from the beta acknowledgement dialog
		// alongside the install steps. Separate from marketplaceUrl, which is the install action.
		listingUrl: {
			type: String,
			default: '',
		},
		listingLinkText: {
			type: String,
			default: 'View the listing',
		},
	},
	data () {
		return {
			betaDialogOpen: false,
		};
	},
	watch: {
		// If access is revoked live while the beta acknowledgement dialog is open,
		// close it so a now-denied user isn't left on the marketplace prompt and
		// betaDialogOpen isn't left stale for a later re-grant.
		disabled (isDisabled: boolean) {
			if (isDisabled) {
				this.betaDialogOpen = false;
			}
		},
	},
	methods: {
		onTutorialClick () {
			if (this.tutorialUrl) {
				window.open(this.tutorialUrl, '_blank', 'noopener,noreferrer');
			}
		},
		// Launched plugins skip the beta acknowledgement and open the marketplace directly.
		onLaunchedClick () {
			if (this.marketplaceUrl) {
				window.open(this.marketplaceUrl, '_blank', 'noopener,noreferrer');
			}
		},
	},
	components: {
		BetaPluginDialog,
	},
});
